import React, { useState } from 'react';

const NewsletterSignup = ({
  post,
  title = 'Get practical guides in your inbox',
  subtitle = 'Short, useful guides on integration, health, and life in Germany — for African families. No spam.',
  benefits = [
    { title: 'Integration tips', description: 'Step-by-step guides for navigating Germany.' },
    { title: 'Health guidance', description: 'Nurse-led wellness advice for your family.' },
    { title: 'Community updates', description: 'Stories and resources from the community.' },
  ],
  successTitle = "You're in.",
  successBody = 'Watch your inbox for practical guidance on life in Germany.',
  ctaLabel = 'Send me the guides',
  source = 'website',
  tag = 'newsletter',
  endpoint = process.env.REACT_APP_SIGNUP_ENDPOINT || '/api/subscribe',
  successLinkLabel = '',
  successLinkHref = '',
  onSuccess,
}) => {
  const [email, setEmail] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          source,
          tag,
          postSlug: post?.slug || '',
        }),
      });

      const result = await response.json();
      if (!result.ok) {
        throw new Error(result.message || 'Failed to subscribe.');
      }
      
      setIsSubscribed(true);

      if (typeof onSuccess === 'function') {
        onSuccess(result);
      }
      
      // Analytics tracking
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'sign_up', {
          method: 'newsletter',
          content_type: 'article',
          item_id: post?.slug
        });
      }
      
      // Clear form
      setEmail('');
      
    } catch (err) {
      setError(err?.message || 'Failed to subscribe. Please try again.');
      console.error('Newsletter signup error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubscribed) {
    return (
      <div
        className="border border-[#8C7B6B]/30 bg-white/70 p-6 text-center"
      >
        <h3 className="font-display text-2xl text-[#2E4036] mb-2">
          {successTitle}
        </h3>
        <p className="text-[#4A4541]">
          {successBody}
        </p>
        {successLinkHref && successLinkLabel && (
          <a
            href={successLinkHref}
            className="inline-flex items-center justify-center mt-4 px-6 py-3 border border-[#8C7B6B] text-xs uppercase tracking-[0.25em] text-[#2E4036] hover:bg-[#ede9df] transition-colors"
          >
            {successLinkLabel}
          </a>
        )}
      </div>
    );
  }

  return (
    <div
      className="border border-[#8C7B6B]/30 bg-white/70 p-6"
    >
        <div className="text-center mb-4">
          <h3 className="font-display text-2xl text-[#2E4036] mb-2">
            {title}
          </h3>
          <p className="text-[#4A4541]">
            {subtitle}
          </p>
        </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="email" className="block text-xs uppercase tracking-[0.2em] text-[#8C7B6B] mb-2">
            Email Address
          </label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            className="w-full px-4 py-3 border border-[#8C7B6B]/30 bg-white focus:outline-none focus:ring-2 focus:ring-[#CC5833]"
            required
          />
        </div>

        {error && (
          <div
            className="text-[#b4533c] text-sm bg-[#fbf4f2] p-3"
          >
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={isLoading}
          className="w-full bg-[#2E4036] text-[#F2F0E9] py-3 px-6 uppercase tracking-[0.25em] text-xs hover:bg-[#1a2a20] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isLoading ? (
            <span className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-[#F2F0E9]/30 border-t-[#F2F0E9] mr-2"></div>
              Subscribing...
            </span>
          ) : (
            ctaLabel
          )}
        </button>
      </form>

      <div className="mt-4 text-center">
        <p className="text-xs text-[#8C7B6B] uppercase tracking-[0.2em]">
          We respect your privacy. Unsubscribe anytime.
        </p>
      </div>

      {/* Benefits */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
        {benefits.map((benefit) => (
          <div key={benefit.title} className="text-center">
            <h4 className="font-display text-lg text-[#2E4036]">{benefit.title}</h4>
            <p className="text-sm text-[#4A4541]">{benefit.description}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default NewsletterSignup; 
